import { useState, useMemo, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { slugToBackendCategory } from '@/src/helpers/categories';
import { getLocalizedPath } from '@/src/i18n/pathnames';
import { getLocalizedCategorySlug } from '@/src/i18n/categorySlugTranslations';
import { locales, defaultLocale, type Locale } from '@/src/i18n/config';
import { getCountryCookie } from '@/src/helpers/countryCookie';
import { PaginatedCards } from '@/src/types/card';
import { LISTING_DEFAULT_ITEMS_PER_PAGE } from '@/constants';

interface UseProductFiltersProps {
  categoryFromUrl?: string;
  countryFromUrl?: string;
  initialProducts?: PaginatedCards;
}

const ITEMS_PER_PAGE_OPTIONS = [20, 40, 60, 80, 100];

export const useProductFilters = ({
  categoryFromUrl,
  countryFromUrl,
  initialProducts
}: UseProductFiltersProps = {}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  
  // Extract current locale from URL
  const pathSegments = pathname?.split('/').filter(Boolean) || [];
  const currentLocale = (pathSegments[0] && locales.includes(pathSegments[0] as Locale) 
    ? pathSegments[0] 
    : defaultLocale) as Locale;


  const [page, setPage] = useState(parseInt(searchParams?.get('page') || '1'));
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categoryFromUrl || null
  );
  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    countryFromUrl ? countryFromUrl.toUpperCase() : (searchParams?.get('country')?.toUpperCase() || getCountryCookie() || null)
  );

  // A navigation that only changes the query string (header search, back
  // and forward) keeps this component mounted, so the state follows the URL.
  useEffect(() => {
    // The country the server applied: the URL's, else the cookie's.
    const appliedCountry = searchParams?.get('country')?.toUpperCase() || getCountryCookie() || null;
    if (appliedCountry !== selectedCountry) {
      setSelectedCountry(appliedCountry);
    }
    setPage(parseInt(searchParams?.get('page') || '1'));
    setShowDiscount(searchParams?.get('discount') === 'true');
    setShowInStock(searchParams?.get('instock') === 'true');
    setSearchQuery(searchParams?.get('search') || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const urlCountry = searchParams?.get('country')?.toUpperCase() || null;
    if (selectedCountry && !urlCountry) {
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.set('country', selectedCountry);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showDiscount, setShowDiscount] = useState<boolean>(
    searchParams?.get('discount') === 'true'
  );
  const [showInStock, setShowInStock] = useState<boolean>(
    searchParams?.get('instock') === 'true'
  );
  const [searchQuery, setSearchQuery] = useState<string | null>(
    searchParams?.get('search') || null
  );

  // Get current mobile state directly - no hook needed for this use case
  const getCurrentIsMobile = () => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 1000px)').matches;
    }
    return false; // Server-side default to desktop
  };

  // The page size is whatever the server component fetched with: it parses and
  // clamps ?limit= itself, and the grid renders exactly the items it returned.
  // Deriving it here (instead of re-reading the URL) keeps the card count, the
  // per-page select and the pagination from ever disagreeing.
  const effectiveCardsPerPage = initialProducts?.limit || LISTING_DEFAULT_ITEMS_PER_PAGE;

  // Direct filter params - no useEffect chain needed
  const filterParams = useMemo(() => {
    const offset = (page - 1) * effectiveCardsPerPage;
    const params: Record<string, unknown> = {
      limit: effectiveCardsPerPage,
      offset
    };

    if (selectedCategory) params.category = slugToBackendCategory(selectedCategory);
    if (selectedCountry) params.country = selectedCountry;
    if (showDiscount) params.discount = true;
    if (showInStock) params.instock = true;
    if (searchQuery) params.search = searchQuery;

    return params;
  }, [page, selectedCategory, selectedCountry, showDiscount, showInStock, searchQuery, effectiveCardsPerPage]);

  // URL update function
  const updateURL = (
    newCategory?: string | null,
    newCountry?: string | null,
    newDiscount?: boolean,
    newPage?: number,
    newSearch?: string | null,
    newLimit?: number,
    newInStock?: boolean
  ) => {
    const category = newCategory !== undefined ? newCategory : selectedCategory;
    const country = newCountry !== undefined ? newCountry : selectedCountry;
    const discount = newDiscount !== undefined ? newDiscount : showDiscount;
    const pageNum = newPage !== undefined ? newPage : page;
    const search = newSearch !== undefined ? newSearch : searchQuery;
    const limit = newLimit !== undefined ? newLimit : effectiveCardsPerPage;
    const instock = newInStock !== undefined ? newInStock : showInStock;

    const params = new URLSearchParams();
    if (discount) params.set('discount', 'true');
    if (instock) params.set('instock', 'true');
    if (search) params.set('search', search);
    if (pageNum > 1) params.set('page', pageNum.toString());
    if (limit !== LISTING_DEFAULT_ITEMS_PER_PAGE) params.set('limit', limit.toString());

    if (country) params.set('country', country.toUpperCase());

    const updatedQueryString = params.toString();
    
    // Build localized URL
    const localizedGiftcardsPath = getLocalizedPath('/giftcards', currentLocale);
    const localizedCategorySlug = category ? getLocalizedCategorySlug(category, currentLocale) : null;
    
    let baseUrl: string;
    if (currentLocale === defaultLocale) {
      baseUrl = localizedCategorySlug ? `${localizedGiftcardsPath}/${localizedCategorySlug}` : localizedGiftcardsPath;
    } else {
      baseUrl = localizedCategorySlug 
        ? `/${currentLocale}${localizedGiftcardsPath}/${localizedCategorySlug}` 
        : `/${currentLocale}${localizedGiftcardsPath}`;
    }
    
    const fullUrl = updatedQueryString ? `${baseUrl}?${updatedQueryString}` : baseUrl;
    router.push(fullUrl, { scroll: false });
  };

  // Handle items per page change - must be passed fetchCards from parent
  const createHandleItemsPerPageChange = (fetchCards: (params?: Record<string, unknown>) => void) => {
    return (newLimit: number) => {
      setPage(1); // Reset to first page when changing items per page
      updateURL(undefined, undefined, undefined, 1, undefined, newLimit);

      // Directly fetch cards with updated params
      const updatedParams = { ...filterParams };
      updatedParams.limit = newLimit;
      updatedParams.offset = 0;

      fetchCards(updatedParams);
    };
  };

  return {
    // Filter state
    page,
    selectedCategory,
    selectedCountry,
    showDiscount,
    showInStock,
    searchQuery,
    filterParams,
    cardsPerPage: effectiveCardsPerPage,
    isMobile: getCurrentIsMobile(),

    // Filter setters
    setPage,
    setSelectedCategory,
    setSelectedCountry,
    setShowDiscount,
    setShowInStock,
    setSearchQuery,
    createHandleItemsPerPageChange,

    // Constants
    ITEMS_PER_PAGE_OPTIONS,

    // Utilities
    updateURL
  };
};
